import React, { useState, useEffect, useCallback } from "react";
import MainContainer from "../../components/MainContainer";
import { userDetailContext } from "../../App";
import { headerContract } from "../../data/modules/contract_header";
import { useContext } from "react";
import { useNavigate } from 'react-router-dom';
import {
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    IconButton,
    Chip,
    Typography,
    TextField,
    CircularProgress,
    Tooltip,
    Card,
    TablePagination,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Backdrop,
    Tabs,
    Tab
} from "@material-ui/core";
import { Alert } from "@mui/material";
import { makeStyles } from "@material-ui/core/styles";
import VisibilityIcon from '@material-ui/icons/Visibility';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import DescriptionIcon from '@material-ui/icons/Description';
import SearchIcon from '@material-ui/icons/Search';
import RefreshIcon from '@material-ui/icons/Refresh';
import WarningIcon from '@material-ui/icons/Warning';
import FilterListIcon from '@material-ui/icons/FilterList';
import ClearIcon from '@material-ui/icons/Clear';
import HistoryIcon from '@material-ui/icons/History';
import AddIcon from '@material-ui/icons/Add';
import LockIcon from '@material-ui/icons/Lock';
import UserService from "../../services/UserService";
import ConsultationHistory from "./ConsultationHistory";
import CreateAttestationDialog from "./CreateAttestationDialog";
import PasswordDialog from "./PasswordDialog";

const useStyles = makeStyles((theme) => ({
    container: {
        padding: theme.spacing(3),
    },
    tableContainer: {
        marginTop: theme.spacing(2),
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        position: 'relative',
        zIndex: 1,
    },
    table: {
        minWidth: 650,
    },
    statusChip: {
        fontWeight: 'bold',
    },
    statusComplete: {
        backgroundColor: '#4caf50',
        color: 'white',
    },
    statusMissing: {
        backgroundColor: '#f44336',
        color: 'white',
    },
    statusPartial: {
        backgroundColor: '#ff9800',
        color: 'white',
    },
    statsCard: {
        padding: theme.spacing(2),
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'transform 0.2s',
        '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: theme.shadows[4],
        },
    },
    statsNumber: {
        fontSize: '2rem',
        fontWeight: 'bold',
        color: theme.palette.primary.main,
    },
    statsLabel: {
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(1),
    },
    filterContainer: {
        marginBottom: theme.spacing(3),
        padding: theme.spacing(2),
        backgroundColor: '#f5f5f5',
        borderRadius: '8px',
    },
    filterTitle: {
        marginBottom: theme.spacing(2),
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    activeFilters: {
        marginTop: theme.spacing(2),
        display: 'flex',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
    },
    activeFilterChip: {
        backgroundColor: theme.palette.primary.light,
        color: 'white',
        '&:hover': {
            backgroundColor: theme.palette.primary.main,
        },
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: '#fff',
    },
    tabs: {
        marginBottom: theme.spacing(2),
        backgroundColor: 'white',
        borderRadius: '8px',
    },
    accessDenied: {
        textAlign: 'center',
        padding: theme.spacing(8),
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
    },
}));

export default function ContractstgList(props) {
    const classes = useStyles();
    const navigate = useNavigate();
    
    // États
    const [stagiaires, setStagiaires] = useState([]);
    const [filteredStagiaires, setFilteredStagiaires] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // État pour l'accès sécurisé à la liste
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
    const [isAccessGranted, setIsAccessGranted] = useState(false);
    const [accessChecking, setAccessChecking] = useState(true);
    
    // États pour les documents sécurisés
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
    const [selectedStagiaireForHistory, setSelectedStagiaireForHistory] = useState(null);
    const [createAttestationOpen, setCreateAttestationOpen] = useState(false);
    
    // États pour les filtres
    const [searchNom, setSearchNom] = useState('');
    const [searchMatricule, setSearchMatricule] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [activeTab, setActiveTab] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [stats, setStats] = useState({
        total: 0,
        complete: 0,
        missing: 0,
        partial: 0
    });
    const [refreshKey, setRefreshKey] = useState(0);

    // Vérifier si la session est encore valide
    const isSessionValid = () => {
        const token = sessionStorage.getItem('stagiaire_access_token');
        const expiry = sessionStorage.getItem('stagiaire_access_expiry');
        
        if (!token || !expiry) {
            return false;
        }
        
        const now = Date.now();
        const expiryTime = parseInt(expiry);
        
        if (now >= expiryTime) {
            sessionStorage.removeItem('stagiaire_access_token');
            sessionStorage.removeItem('stagiaire_access_expiry');
            return false;
        }
        
        return true;
    };

    // Vérifier et gérer l'accès à la liste
    useEffect(() => {
        const checkAccess = () => {
            if (isSessionValid()) {
                setIsAccessGranted(true);
                setAccessChecking(false);
                loadStagiaires();
            } else {
                setIsAccessGranted(false);
                setAccessChecking(false);
                setPasswordDialogOpen(true);
            }
        };
        
        checkAccess();
    }, []);

    // Charger la liste des stagiaires
    const loadStagiaires = useCallback(async () => {
        if (!isAccessGranted) return;
        
        setLoading(true);
        setError(null);
        try {
            const response = await UserService.contractstg();
            if (response && response.status) {
                const data = response.data || response;
                setStagiaires(data);
                updateStats(data);
                applyFiltersWithParams(data, searchNom, searchMatricule, statusFilter);
            } else {
                setError("Erreur lors du chargement des données");
            }
        } catch (err) {
            if (err.response?.status === 401 || err.message?.includes('session')) {
                handleSessionExpired();
            } else {
                setError("Impossible de charger la liste des stagiaires");
            }
        } finally {
            setLoading(false);
        }
    }, [isAccessGranted, searchNom, searchMatricule, statusFilter]);

    // Gérer l'expiration de session
    const handleSessionExpired = () => {
        sessionStorage.removeItem('stagiaire_access_token');
        sessionStorage.removeItem('stagiaire_access_expiry');
        setIsAccessGranted(false);
        setPasswordDialogOpen(true);
    };

    // Charger les données quand l'accès est accordé
    useEffect(() => {
        if (isAccessGranted) {
            loadStagiaires();
        }
    }, [loadStagiaires, isAccessGranted, refreshKey]);

    // Mettre à jour les statistiques
    const updateStats = (data) => {
        const total = data.length;
        const complete = data.filter(s => (s.has_contrat || s.file_contrat) && 
                                          (s.has_assurance || s.file_attestation) && 
                                          (s.has_diplome || s.file_diplome)).length;
        const missing = data.filter(s => !(s.has_contrat || s.file_contrat) && 
                                         !(s.has_assurance || s.file_attestation) && 
                                         !(s.has_diplome || s.file_diplome)).length;
        const partial = total - complete - missing;
        
        setStats({ total, complete, missing, partial });
    };

    // Fonction pour appliquer les filtres
    const applyFiltersWithParams = (data, nom, matricule, status) => {
        let filtered = [...data];
        
        if (nom) {
            filtered = filtered.filter(stagiaire => 
                (stagiaire.user_first_name?.toLowerCase() || '').includes(nom.toLowerCase()) ||
                (stagiaire.user_last_name?.toLowerCase() || '').includes(nom.toLowerCase()) ||
                `${stagiaire.user_last_name?.toLowerCase()} ${stagiaire.user_first_name?.toLowerCase()}`.includes(nom.toLowerCase()) ||
                `${stagiaire.user_first_name?.toLowerCase()} ${stagiaire.user_last_name?.toLowerCase()}`.includes(nom.toLowerCase()) ||
                (stagiaire.full_name?.toLowerCase() || '').includes(nom.toLowerCase())
            );
        }
        
        if (matricule) {
            filtered = filtered.filter(stagiaire => 
                (stagiaire.cin?.toLowerCase() || '').includes(matricule.toLowerCase()) ||
                (stagiaire.registration_number?.toLowerCase() || '').includes(matricule.toLowerCase())
            );
        }
        
        if (status !== 'all') {
            filtered = filtered.filter(stagiaire => {
                const hasContrat = stagiaire.has_contrat || stagiaire.file_contrat;
                const hasAssurance = stagiaire.has_assurance || stagiaire.file_attestation;
                const hasDiplome = stagiaire.has_diplome || stagiaire.file_diplome;
                
                if (status === 'complete') {
                    return hasContrat && hasAssurance && hasDiplome;
                } else if (status === 'missing') {
                    return !hasContrat && !hasAssurance && !hasDiplome;
                } else if (status === 'partial') {
                    return (hasContrat || hasAssurance || hasDiplome) && 
                           !(hasContrat && hasAssurance && hasDiplome);
                }
                return true;
            });
        }
        
        setFilteredStagiaires(filtered);
        setPage(0);
    };

    const applyFilters = () => {
        applyFiltersWithParams(stagiaires, searchNom, searchMatricule, statusFilter);
    };

    useEffect(() => {
        if (isAccessGranted && stagiaires.length > 0) {
            applyFilters();
        }
    }, [searchNom, searchMatricule, statusFilter, stagiaires, isAccessGranted]);

    const handleFilter = () => {
        applyFilters();
    };

    const handleResetFilters = () => {
        setSearchNom('');
        setSearchMatricule('');
        setStatusFilter('all');
        setActiveTab(0);
        setFilteredStagiaires(stagiaires);
        setPage(0);
    };

    const handleViewDetails = (stagiaire) => {
        navigate('/contract_stg/' + stagiaire.user_id);
    };

    // Fonction pour voir l'historique
    const handleViewHistory = (stagiaire) => {
        setSelectedStagiaireForHistory(stagiaire);
        setHistoryDialogOpen(true);
    };

    const handleCloseHistoryDialog = () => {
        setHistoryDialogOpen(false);
        setSelectedStagiaireForHistory(null);
    };

    const getStagiaireStatus = (stagiaire) => {
        const hasContrat = stagiaire.has_contrat || stagiaire.file_contrat;
        const hasAssurance = stagiaire.has_assurance || stagiaire.file_attestation;
        const hasDiplome = stagiaire.has_diplome || stagiaire.file_diplome;
        
        if (hasContrat && hasAssurance && hasDiplome) {
            return { label: 'Complet', color: 'statusComplete', icon: <CheckCircleIcon /> };
        } else if (!hasContrat && !hasAssurance && !hasDiplome) {
            return { label: 'Manquant', color: 'statusMissing', icon: <CancelIcon /> };
        } else {
            return { label: 'Partiel', color: 'statusPartial', icon: <WarningIcon /> };
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            applyFilters();
        }
    };

    const handleRefresh = () => {
        if (!isSessionValid()) {
            handleSessionExpired();
            return;
        }
        loadStagiaires();
    };

    // Accès à la liste accordé
    const handleListAccessGranted = () => {
        setIsAccessGranted(true);
    };

    // Déconnexion
    const handleLogout = () => {
        sessionStorage.removeItem('stagiaire_access_token');
        sessionStorage.removeItem('stagiaire_access_expiry');
        setIsAccessGranted(false);
        setPasswordDialogOpen(true);
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        switch(newValue) {
            case 0:
                setStatusFilter('all');
                break;
            case 1:
                setStatusFilter('complete');
                break;
            case 2:
                setStatusFilter('partial');
                break;
            case 3:
                setStatusFilter('missing');
                break;
            default:
                break;
        }
    };

    if (accessChecking) {
        return (
            <MainContainer {...headerContract}>
                <div className={classes.loadingContainer}>
                    <CircularProgress size={60} />
                </div>
            </MainContainer>
        );
    }

    if (!isAccessGranted) {
        return (
            <MainContainer {...headerContract}>
                <div className={classes.accessDenied}>
                    <LockIcon style={{ fontSize: 80, color: '#ccc', marginBottom: 20 }} />
                    <Typography variant="h5" color="textSecondary" gutterBottom>
                        Accès sécurisé requis
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                        Veuillez vous authentifier pour accéder à cette page
                    </Typography>
                </div>
                
                <PasswordDialog
                    open={passwordDialogOpen}
                    onClose={() => navigate('/home')}
                    onSuccess={handleListAccessGranted}
                    title="Accès à la gestion des contrats des stagiaires"
                    typecontrat="Stagiaire"
                    lienapresvalidate="/contract_stg"
                />
            </MainContainer>
        );
    }

    return (
        <MainContainer {...headerContract}>
            <div className={classes.container}>
                <Backdrop className={classes.backdrop} open={loading}>
                    <CircularProgress color="inherit" />
                </Backdrop>

                {/* Statistiques */}
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card className={classes.statsCard} onClick={() => handleTabChange(null, 0)}>
                            <Typography className={classes.statsNumber}>{stats.total}</Typography>
                            <Typography className={classes.statsLabel}>Total Stagiaires</Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card className={classes.statsCard} style={{ borderTop: '4px solid #4caf50' }} onClick={() => handleTabChange(null, 1)}>
                            <Typography className={classes.statsNumber} style={{ color: '#4caf50' }}>{stats.complete}</Typography>
                            <Typography className={classes.statsLabel}>Dossiers Complets</Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card className={classes.statsCard} style={{ borderTop: '4px solid #ff9800' }} onClick={() => handleTabChange(null, 2)}>
                            <Typography className={classes.statsNumber} style={{ color: '#ff9800' }}>{stats.partial}</Typography>
                            <Typography className={classes.statsLabel}>Dossiers Partiels</Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card className={classes.statsCard} style={{ borderTop: '4px solid #f44336' }} onClick={() => handleTabChange(null, 3)}>
                            <Typography className={classes.statsNumber} style={{ color: '#f44336' }}>{stats.missing}</Typography>
                            <Typography className={classes.statsLabel}>Dossiers Manquants</Typography>
                        </Card>
                    </Grid>
                </Grid>

                {/* Barre d'info session */}
                <Box display="flex" justifyContent="flex-end" mb={1}>
                    <Tooltip title="Déconnexion sécurisée">
                        <Button size="small" color="secondary" onClick={handleLogout}>
                            <LockIcon style={{ fontSize: 16, marginRight: 4 }} />
                            Quitter l'accès sécurisé
                        </Button>
                    </Tooltip>
                </Box>

                {/* Onglets */}
                <Paper className={classes.tabs}>
                    <Tabs value={activeTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary" variant="fullWidth">
                        <Tab label="Tous" />
                        <Tab label="Complets" />
                        <Tab label="Partiels" />
                        <Tab label="Manquants" />
                    </Tabs>
                </Paper>

                {/* Panneau de filtres */}
                <Paper className={classes.filterContainer}>
                    <div className={classes.filterTitle}>
                        <FilterListIcon color="primary" />
                        <Typography variant="h6">Filtres de recherche</Typography>
                    </div>
                    
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={2}>
                            <Button
                                fullWidth
                                variant="contained"
                                color="primary"
                                onClick={handleFilter}
                                startIcon={<SearchIcon />}
                                style={{ height: '56px' }}
                            >
                                Filtrer
                            </Button>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <Button
                                fullWidth
                                variant="outlined"
                                color="primary"
                                onClick={handleResetFilters}
                                startIcon={<ClearIcon />}
                                style={{ height: '56px' }}
                            >
                                Réinitialiser
                            </Button>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <Button
                                fullWidth
                                variant="outlined"
                                color="default"
                                onClick={handleRefresh}
                                startIcon={<RefreshIcon />}
                                style={{ height: '56px' }}
                            >
                                Rafraîchir
                            </Button>
                        </Grid>

                        <Grid item xs={12} md={6} />

                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Rechercher par nom ou prénom..."
                                label="Nom / Prénom"
                                value={searchNom}
                                onChange={(e) => setSearchNom(e.target.value)}
                                onKeyPress={handleKeyPress}
                                size="small"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchNom && (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setSearchNom('')}>
                                                <ClearIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Rechercher par CIN ou matricule..."
                                label="CIN / Matricule"
                                value={searchMatricule}
                                onChange={(e) => setSearchMatricule(e.target.value)}
                                onKeyPress={handleKeyPress}
                                size="small"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchMatricule && (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setSearchMatricule('')}>
                                                <ClearIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth size="small" variant="outlined">
                                <InputLabel>Statut du dossier</InputLabel>
                                <Select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    label="Statut du dossier"
                                >
                                    <MenuItem value="all">Tous les statuts</MenuItem>
                                    <MenuItem value="complete">Dossiers complets</MenuItem>
                                    <MenuItem value="partial">Dossiers partiels</MenuItem>
                                    <MenuItem value="missing">Dossiers manquants</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Résultats de recherche */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="body2" color="textSecondary">
                        {filteredStagiaires.length} résultat(s) trouvé(s) sur {stagiaires.length} stagiaires
                    </Typography>
                </Box>

                {/* Tableau des stagiaires */}
                <TableContainer component={Paper} className={classes.tableContainer}>
                    {error ? (
                        <Alert severity="error" style={{ margin: '20px' }}>
                            {error}
                        </Alert>
                    ) : filteredStagiaires.length === 0 && !loading ? (
                        <div style={{ textAlign: 'center', padding: '50px' }}>
                            <Typography variant="h6" color="textSecondary">
                                Aucun stagiaire trouvé avec ces critères
                            </Typography>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={handleResetFilters}
                                style={{ marginTop: '20px' }}
                            >
                                Réinitialiser les filtres
                            </Button>
                        </div>
                    ) : (
                        <>
                            <Table className={classes.table}>
                                <TableHead>
                                    <TableRow style={{ backgroundColor: '#1976d2' }}>
                                        <TableCell style={{ color: 'white', fontWeight: 'bold' }}>N°</TableCell>
                                        <TableCell style={{ color: 'white', fontWeight: 'bold' }}>CIN/Matricule</TableCell>
                                        <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Nom & Prénom</TableCell>
                                        <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Email</TableCell>
                                        <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Téléphone</TableCell>
                                        <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Contrat</TableCell>
                                        <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Assurance</TableCell>
                                        <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Diplôme</TableCell>
                                        <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Statut</TableCell>
                                        <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredStagiaires
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((stagiaire, index) => {
                                            const status = getStagiaireStatus(stagiaire);
                                            return (
                                                <TableRow key={stagiaire.user_id} hover>
                                                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                                    <TableCell>
                                                        {stagiaire.cin || stagiaire.registration_number || '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        <strong>{stagiaire.full_name?.toUpperCase() || `${stagiaire.user_last_name || ''} ${stagiaire.user_first_name || ''}`.trim()}</strong>
                                                    </TableCell>
                                                    <TableCell>{stagiaire.email || stagiaire.user_email || '-'}</TableCell>
                                                    <TableCell>{stagiaire.phone_orange || stagiaire.user_phone || '-'}</TableCell>
                                                    <TableCell align="center">
                                                        {(stagiaire.file_contrat || stagiaire.has_contrat) ? (
                                                            <Tooltip title="Contrat présent">
                                                                <CheckCircleIcon style={{ color: '#4caf50' }} />
                                                            </Tooltip>
                                                        ) : (
                                                            <Tooltip title="Contrat manquant">
                                                                <CancelIcon style={{ color: '#f44336' }} />
                                                            </Tooltip>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {(stagiaire.file_attestation || stagiaire.has_assurance) ? (
                                                            <Tooltip title="Assurance présente">
                                                                <CheckCircleIcon style={{ color: '#4caf50' }} />
                                                            </Tooltip>
                                                        ) : (
                                                            <Tooltip title="Assurance manquante">
                                                                <CancelIcon style={{ color: '#f44336' }} />
                                                            </Tooltip>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {(stagiaire.file_diplome || stagiaire.has_diplome) ? (
                                                            <Tooltip title="Diplôme présent">
                                                                <CheckCircleIcon style={{ color: '#4caf50' }} />
                                                            </Tooltip>
                                                        ) : (
                                                            <Tooltip title="Diplôme manquant">
                                                                <CancelIcon style={{ color: '#f44336' }} />
                                                            </Tooltip>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            icon={status.icon}
                                                            label={status.label}
                                                            className={`${classes.statusChip} ${classes[status.color]}`}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                            <Tooltip title="Voir détails">
                                                                <Button
                                                                    variant="outlined"
                                                                    color="primary"
                                                                    size="small"
                                                                    startIcon={<VisibilityIcon />}
                                                                    onClick={() => handleViewDetails(stagiaire)}
                                                                >
                                                                    Détails
                                                                </Button>
                                                            </Tooltip>
                                                            <Tooltip title="Voir historique">
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    startIcon={<HistoryIcon />}
                                                                    onClick={() => handleViewHistory(stagiaire)}
                                                                >
                                                                    Historique
                                                                </Button>
                                                            </Tooltip>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </Table>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                component="div"
                                count={filteredStagiaires.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                labelRowsPerPage="Lignes par page"
                                labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
                            />
                        </>
                    )}
                </TableContainer>

                {/* Dialogue Historique */}
                <ConsultationHistory
                    open={historyDialogOpen}
                    onClose={handleCloseHistoryDialog}
                    stagiaireId={selectedStagiaireForHistory?.user_id}
                    stagiaireNom={selectedStagiaireForHistory?.full_name}
                />

                {/* Dialogue de création d'attestation */}
                <CreateAttestationDialog
                    open={createAttestationOpen}
                    onClose={() => setCreateAttestationOpen(false)}
                />
            </div>
        </MainContainer>
    );
}